import { Card, Descriptions, Flex, Tag, Typography } from 'antd'
import { Database } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { WorkspaceApp } from 'src/api/facade/types'
import { DateTimeCell, MonoValue } from 'src/components'

import { THIRD_PARTY_CUSTOM_APP_TYPE } from '../constants/constants'

const { Text } = Typography

type Props = {
  app: WorkspaceApp
}

export const AppDetailOverviewTab = ({ app }: Props) => {
  const { t } = useTranslation()
  const isCustom = app.instanceTypeCode === THIRD_PARTY_CUSTOM_APP_TYPE

  return (
    <Flex vertical gap={14}>
      <Card
        size="small"
        variant="outlined"
        title={
          <Flex align="center" gap={6}>
            <Database size={14} color="var(--ant-color-text-secondary)" />
            <Text strong style={{ fontSize: 13 }}>
              {t('workspace.apps.overviewTab.info.title')}
            </Text>
          </Flex>
        }
        styles={{ body: { padding: 14 } }}
      >
        <Descriptions
          column={{ xs: 1, sm: 2, lg: 3 }}
          size="small"
          colon={false}
        >
          <Descriptions.Item label={t('workspace.apps.overviewTab.info.name')}>
            {app.name}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('workspace.apps.overviewTab.info.template')}
          >
            {app.instanceTypeName ?? '—'}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('workspace.apps.overviewTab.info.created')}
          >
            <DateTimeCell
              value={app.createdAt}
              variant="date"
              style={{ fontSize: 13 }}
            />
          </Descriptions.Item>
          <Descriptions.Item
            span={3}
            label={t('workspace.apps.overviewTab.info.description')}
          >
            {app.description?.trim() || '—'}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              isCustom
                ? t('workspace.apps.overviewTab.info.appUrl')
                : t('workspace.apps.overviewTab.info.domain')
            }
          >
            {isCustom ? (
              app.appUrl ? (
                <MonoValue ellipsis>{app.appUrl}</MonoValue>
              ) : (
                <Text type="secondary">—</Text>
              )
            ) : app.domain ? (
              <MonoValue ellipsis>{app.domain}</MonoValue>
            ) : (
              <Text type="secondary">—</Text>
            )}
          </Descriptions.Item>
          {app.isPublic != null ? (
            <Descriptions.Item
              label={t('workspace.apps.overviewTab.info.visibility')}
            >
              <Tag
                style={{
                  marginInlineEnd: 0,
                  fontSize: 11,
                  background: app.isPublic
                    ? 'var(--ant-color-primary-bg)'
                    : 'var(--ant-color-fill-tertiary)',
                  color: app.isPublic
                    ? 'var(--ant-color-primary)'
                    : 'var(--ant-color-text-secondary)',
                  border: 'none'
                }}
              >
                {app.isPublic
                  ? t('workspace.apps.visibility.public')
                  : t('workspace.apps.visibility.private')}
              </Tag>
            </Descriptions.Item>
          ) : null}
          <Descriptions.Item
            span={app.isPublic != null ? 1 : 2}
            label={t('workspace.apps.overviewTab.info.token')}
          >
            {app.token ? (
              <MonoValue ellipsis>{app.token}</MonoValue>
            ) : (
              <Text type="secondary">—</Text>
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </Flex>
  )
}
